import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, Signal, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIconComponent, provideIcons, provideNgIconLoader } from '@ng-icons/core';
import { lucideCircleAlert, lucideGoal } from '@ng-icons/lucide';

import { FocusOnlyRadioGroupDirective } from 'app/home/common/directives/focus-only-radio-group.directive';
import { ReadonlyRadioDirective } from 'app/home/common/directives/readonly-radio.directive';
import { BaseQuoteForm } from 'app/home/create-quote/form-config/quote-form-base';
import { QuoteFormContextDecorator } from 'app/home/create-quote/form-config/quote-form-decorator';
import { Lockable, LockedQuoteAlertComponent, WithLockableFields } from 'app/home/create-quote/form-locking';
import { isAddressInHD3 } from 'app/home/create-quote/forms/policy-type/policy-type-utils';
import { PolicyTypeQuoteFormInput, PolicyTypeValue } from 'app/home/create-quote/forms/policy-type/policy-type.types';
import { FormCardComponent } from 'app/home/create-quote/forms/shared/form-card.component';
import { QuoteDraftState } from 'app/home/global-store/quote-draft/quote-draft.model';
import { QuoteFormOptionsStore } from 'app/home/global-store/quote-form-options';
import { FeatureFlagSignalsService } from 'app/shared/services/splitIO/feature-flag.service';

@Component({
  selector: 'app-policy-type',
  imports: [FormCardComponent, NgIconComponent, ReactiveFormsModule, ReadonlyRadioDirective, LockedQuoteAlertComponent, FocusOnlyRadioGroupDirective],
  templateUrl: './policy-type.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    provideIcons({ lucideCircleAlert, lucideGoal }),
    provideNgIconLoader((name) => {
      const http = inject(HttpClient);
      return http.get(`/assets/icons/${name}.svg`, { responseType: 'text' });
    }),
  ],
  styles: [
    `
      :host {
        display: contents;
      }
    `,
  ],
})
@QuoteFormContextDecorator({
  legacyShape: '/quote/policy_type',
  isAutoAdvanced: true,
})
export class QuoteFormPolicyTypeComponent extends BaseQuoteForm<PolicyTypeQuoteFormInput> implements OnInit, Lockable {
  private featureFlagSignalsService = inject(FeatureFlagSignalsService);
  public isAutoAdvanceEnabled = this.featureFlagSignalsService.isAutoAdvanceEnabled;

  private optionsStore = inject(QuoteFormOptionsStore);
  public formDataIn: PolicyTypeQuoteFormInput = this.quoteDraftStore.policyTypeQuoteFormSelector();
  public isLocked: Signal<{ [key: string]: boolean }> = WithLockableFields.createIsLocked.call(this);
  public isFieldLocked = WithLockableFields.isFieldLocked;
  public allFieldsLocked = WithLockableFields.allFieldsLocked;

  public policyTypeOptions = this.optionsStore.policyTypeQuoteFormOptionsSelector();

  public formDefinition = {
    policyType: new FormControl<PolicyTypeValue | undefined>(undefined, Validators.required),
  };
  public policyTypeForm = new FormGroup(this.formDefinition);

  public override autoAdvanceControlRef = this.policyTypeForm.controls.policyType;

  private addressState = this.quoteDraftStore.address()?.state ?? '';

  public ngOnInit() {
    if (this.formDataIn && this.isFieldLocked('policyType')) {
      this.policyTypeForm.controls.policyType.setValue(this.formDataIn.policyType);
    }
  }

  private getFormValues(): Pick<QuoteDraftState, 'policyType'> {
    const { controls } = this.policyTypeForm;
    const formValues = {
      policyType: controls.policyType.value ?? undefined,
    };

    return formValues;
  }

  public getFormControls() {
    return this.policyTypeForm.controls;
  }

  public handleSubmit(e: Event) {
    e.preventDefault();
    this.policyTypeForm.markAllAsTouched();
    if (this.policyTypeForm.valid) {
      if (this.allFieldsLocked()) {
        super.navigateForward();
      } else {
        super.saveFormData(this.getFormValues());
      }
    }
  }

  public get showError(): boolean {
    if (this.isAutoAdvanceEnabled()) {
      return false;
    }
    const control = this.policyTypeForm.get('policyType');
    return !!control?.invalid && !!control?.touched;
  }

  public get isHD3State(): boolean {
    return isAddressInHD3(this.addressState);
  }
}
